import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, resolveComponent as _resolveComponent, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "appointments-list" }
const _hoisted_2 = { class: "appointments-list__header" }
const _hoisted_3 = /*#__PURE__*/_createElementVNode("h2", null, " Следующий приём ", -1)
const _hoisted_4 = /*#__PURE__*/_createElementVNode("div", { class: "spacer" }, null, -1)
const _hoisted_5 = {
  key: 0,
  class: "appointments-list__pagination"
}
const _hoisted_6 = /*#__PURE__*/_createElementVNode("div", { class: "spacer" }, null, -1)
const _hoisted_7 = /*#__PURE__*/_createElementVNode("div", { class: "spacer" }, null, -1)

import { ref, watch, h } from 'vue';
import { pick } from 'lodash';
import moment from 'moment';
import axios from 'axios';
import {
    Button as AButton,
    message as antMessage,
    Modal,
    Empty as AEmpty,
} from 'ant-design-vue';
import DeleteOutlined from '@ant-design/icons-vue/DeleteOutlined';
import InfoCircleOutlined from '@ant-design/icons-vue/InfoCircleOutlined';
import EditOutlined from '@ant-design/icons-vue/EditOutlined';
import RedoOutlined from '@ant-design/icons-vue/RedoOutlined';
import DownOutlined from '@ant-design/icons-vue/DownOutlined';
import {
    IAppointment,
    IFatAppointment,
    IPartialAppointment,
} from '@tools/types';
import ModalController from
    'vue-web-components/src/components/patient-services/assignments/ModalController.vue';
import {
    createAppointment,
    getAppointments,
    updateAppointmentWithProtection as updateAppointment,
} from '@api';
import NextAppointment from '@/components/reception/NextAppointment.vue';
import { showErrorNotification } from '@/utils';


export default /*#__PURE__*/_defineComponent({
  __name: 'AppointmentsList',
  props: {
    patientId: null,
    protocolId: null,
    isReadMode: { type: [Boolean, null] }
  },
  setup(__props: any) {

const props = __props as {
    patientId: number;
    protocolId: number | null;
    isReadMode: boolean | undefined;
}



const columns = [{
    title: 'Врач/специальность',
    slots: { customRender: 'doctorName' },
}, {
    title: 'Дата приёма',
    slots: { customRender: 'receptionDate' },
}, {
    title: 'Назначил',
    dataIndex: 'userCreateName',
}, {
    title: 'Подтверждено',
    slots: { customRender: 'receptionTime' },
}, {
    width: 40,
    slots: { customRender: 'editAction' },
}, {
    width: 40,
    slots: { customRender: 'removeAction' },
}];
const conflictedColumns = [{
    title: 'Изменено',
    dataIndex: 'field',
}, {
    title: '',
    dataIndex: 'value',
}];
const PAGE_SIZE = 6;
const appointmentTableLocale = {
    emptyText: h(AEmpty, {
        description: 'Не запланировано ни одного приёма.',
        image: AEmpty.PRESENTED_IMAGE_SIMPLE,
    }),
};

const data = ref<IAppointment[]>([]);
const isFetching = ref(true);
const recordsToShow = ref(PAGE_SIZE);
const totalRecordsCount = ref(0);
const appointmentToEdit = ref<IPartialAppointment>();
const nextAppointmentRef = ref();

const conflictMessage = ref('');
const conflictAction = ref<'PUT' | 'DELETE'>();
const conflictedValueList = ref<{
    id: number;
    field: string;
    value: string;
}[]>([]);
const confirmationModalRef = ref();

const pickEssentialData = (record: IFatAppointment) => pick(record, [
    'protocolId',
    'clinicCabinetId',
    'clinicCabinetName',
    'comment',
    'day',
    'id',
    'isRemote',
    'patientId',
    'remoteUrl',
    'specialtyMedId',
    'specialtyMedName',
    'staffUserId',
    'staffUserName',
    'state',
    'stateCode',
    'timeCreate',
    'timeEdit',
    'timeEnd',
    'timeStart',
    'userCreate',
    'userCreateName',
]);

const fetchAppointments = async () => {
    const { patientId } = props;
    if (!patientId) {
        data.value = [];
        return false;
    }
    try {
        isFetching.value = true;
        const response = await getAppointments({
            params: {
                patientId,
                dayFrom: moment().format('YYYY-MM-DD'),
                page: JSON.stringify([0, recordsToShow.value]),
            },
        });
        const newData = response.data.data.map((rec) => pickEssentialData(rec));
        totalRecordsCount.value = response.data.count;
        data.value = newData;
    } catch (err) {
        showErrorNotification('Произошла ошибка при загрузке списка приёмов.');
        return false;
    } finally {
        isFetching.value = false;
    }
    return true;
};

watch(
    () => props.patientId,
    fetchAppointments,
    { immediate: true },
);

const getAppointmentChanges = (
    next: IFatAppointment,
    prev: IPartialAppointment,
) => {
    const list = [];
    if (next.specialtyMedName !== prev.specialtyMedName) {
        list.push({
            id: 1,
            field: 'Специальность',
            value: next.specialtyMedName || 'не указана',
        });
    }
    if (next.staffUserName !== prev.staffUserName) {
        list.push({
            id: 2,
            field: 'Врач',
            value: next.staffUserName || 'не указан',
        });
    }
    if (next.day !== prev.day) {
        list.push({
            id: 3,
            field: 'Дата приёма',
            value: (next.day && moment(next.day).format('DD.MM.YYYY')) || 'не указана',
        });
    }
    if (next.timeStart !== prev.timeStart || next.timeEnd !== prev.timeEnd) {
        list.push({
            id: 4,
            field: 'Время приёма',
            value: next.timeStart ? `${next.timeStart} - ${next.timeEnd}` : 'не указано',
        });
    }
    if (prev.stateCode === 'WAIT' && next.stateCode === 'SCHEDULED') {
        list.push({
            id: 5,
            field: 'Статус приёма',
            value: 'подтверждён',
        });
    } else if (next.stateCode === 'WAIT' && prev.stateCode === 'SCHEDULED') {
        list.push({
            id: 5,
            field: 'Статус приёма',
            value: 'не подтверждён',
        });
    }

    return list;
};

let removeAppointment: (record: IPartialAppointment) => Promise<void>;
let onAppointmentUpdate: (record: IPartialAppointment) => Promise<void>;
let onAppointmentCreate: (appointment: IPartialAppointment) => Promise<void>;

const handleAppointmentUpdateConflict = async (
    newerAppointment: IFatAppointment,
    appointment: IPartialAppointment,
    action: 'DELETE' | 'PUT',
) => {
    const editTime = moment(newerAppointment.timeEdit, 'DD.MM.YYYY HH:mm');
    if (!newerAppointment.id) {
        conflictMessage.value = `Запись на приём к ${
            appointment.staffUserName || appointment.specialtyMedName
        } на ${
            moment(appointment.day).format('DD.MM.YYYY')
        } была удалена пользователем ${
            newerAppointment.userEditName
        } ${editTime.format('DD.MM.YYYY')} в ${editTime.format('HH:mm:ss')}.`;
        conflictAction.value = action;
        conflictedValueList.value = [];

        const confirmed = await confirmationModalRef.value?.show();
        if (!confirmed) {
            return;
        }
        onAppointmentCreate({
            ...appointment,
            id: null,
        });
        return;
    }

    const significantChanges = getAppointmentChanges(
        newerAppointment,
        appointment,
    );
    if (!significantChanges.length) {
        if (action === 'DELETE') {
            removeAppointment({
                ...appointment,
                timeEdit: newerAppointment.timeEdit,
            });
            return;
        }
        onAppointmentUpdate({
            ...appointment,
            timeEdit: newerAppointment.timeEdit,
        });
        return;
    }

    conflictMessage.value = `Запись на приём к ${
        appointment.staffUserName || appointment.specialtyMedName
    } на ${
        moment(appointment.day).format('DD.MM.YYYY')
    } была изменена пользователем ${
        newerAppointment.userEditName
    } ${editTime.format('DD.MM.YYYY')} в ${editTime.format('HH:mm:ss')}.`;
    conflictAction.value = action;
    conflictedValueList.value = significantChanges;

    const confirmed = await confirmationModalRef.value?.show();
    if (!confirmed) {
        return;
    }
    if (action === 'DELETE') {
        removeAppointment({
            ...appointment,
            timeEdit: newerAppointment.timeEdit,
        });
        return;
    }
    onAppointmentUpdate({
        ...appointment,
        timeEdit: newerAppointment.timeEdit,
    });
};

const handleAppointmentUpdateError = (
    err: unknown,
    appointment: IPartialAppointment,
    action: 'DELETE' | 'PUT',
) => {
    const defaultMsg = `Произошла ошибка при попытке ${
        action === 'DELETE' ? 'удаления' : 'сохранения'
    } приёма.`;
    if (!axios.isAxiosError(err) || !err.response) {
        showErrorNotification(defaultMsg);
        return;
    }

    const { status, data: responseData } = err.response;
    if (status !== 409) {
        showErrorNotification(responseData.msg || defaultMsg);
        return;
    }
    handleAppointmentUpdateConflict(responseData.data, appointment, action);
};

removeAppointment = async (record: IPartialAppointment) => {
    if (!record.id) {
        return;
    }

    const ACTION = 'DELETE';
    isFetching.value = true;
    try {
        await updateAppointment(record.id, ACTION, {
            ...record,
            baseTimestamp: moment.utc(record.timeEdit || record.timeCreate, 'DD.MM.YYYY HH:mm').toDate().getTime(),
        });
    } catch (err) {
        handleAppointmentUpdateError(err, record, ACTION);
        return;
    } finally {
        isFetching.value = false;
    }

    nextAppointmentRef.value.clearAppointment(record.id);

    const success = await fetchAppointments();
    if (success) {
        antMessage.success('Готово.');
    }
};

const onRemoveClick = async (record: IPartialAppointment) => {
    const { stateCode } = record;
    Modal.confirm({
        title: 'Внимание!',
        content: stateCode !== 'WAIT'
            ? 'Эта запись уже подтверждена. Хотите продолжить удаление?'
            : 'Запись будет удалена. Хотите продолжить?',
        onOk: () => removeAppointment(record),
    });
};

onAppointmentUpdate = async (record: IPartialAppointment) => {
    if (!record.id) {
        return;
    }

    const ACTION = 'PUT';
    isFetching.value = true;
    try {
        await updateAppointment(record.id, ACTION, {
            ...record,
            baseTimestamp: moment.utc(record.timeEdit || record.timeCreate, 'DD.MM.YYYY HH:mm').toDate().getTime(),
        });
    } catch (err) {
        handleAppointmentUpdateError(err, record, ACTION);
        return;
    } finally {
        isFetching.value = false;
    }

    appointmentToEdit.value = undefined;

    const success = await fetchAppointments();
    if (success) {
        antMessage.success('Готово.');
    }
};

onAppointmentCreate = async (appointment: IPartialAppointment) => {
    isFetching.value = true;
    try {
        await createAppointment(appointment);
    } catch (err) {
        showErrorNotification('Не удалось сохранить запись на приём.');
        return;
    } finally {
        isFetching.value = false;
    }

    nextAppointmentRef.value.clearAppointment();

    const success = await fetchAppointments();
    if (success) {
        antMessage.success('Готово.');
    }
};

const onCancelClick = () => {
    appointmentToEdit.value = undefined;
};

const onShowMoreAppointmentsClick = () => {
    recordsToShow.value += PAGE_SIZE;
    fetchAppointments();
};

const onRefreshClick = async () => {
    const success = await fetchAppointments();
    if (success) {
        antMessage.success('Обновлено.');
    }
};

const onEditClick = (record: IAppointment) => {
    appointmentToEdit.value = record;
};

const getRuFormattedDate = (date: string) => moment(date, 'YYYY-MM-DD').format('DD.MM.YYYY');

const getReceptionStatusTime = (record: IAppointment) => {
    if (record.stateCode === 'SCHEDULED'
        || record.stateCode === 'STARTED'
        || record.stateCode === 'FINISHED'
    ) {
        return `Да (${record.timeStart} - ${record.timeEnd})`;
    }
    return 'Нет';
};

return (_ctx: any,_cache: any) => {
  const _component_a_table = _resolveComponent("a-table")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _hoisted_3,
      _hoisted_4,
      _createVNode(_unref(AButton), {
        disabled: isFetching.value,
        "data-test-id": "update-appointments-btn",
        onClick: onRefreshClick
      }, {
        icon: _withCtx(() => [
          _createVNode(_unref(RedoOutlined))
        ]),
        default: _withCtx(() => [
          _createTextVNode(" Обновить список приёмов ")
        ]),
        _: 1
      }, 8, ["disabled"])
    ]),
    _createVNode(_component_a_table, {
      columns: columns,
      pagination: false,
      "data-source": data.value,
      locale: appointmentTableLocale,
      "row-key": "id",
      size: "small"
    }, {
      doctorName: _withCtx(({ record }) => [
        _createTextVNode(_toDisplayString(record.staffUserName || record.specialtyMedName), 1)
      ]),
      receptionDate: _withCtx(({ record }) => [
        _createTextVNode(_toDisplayString(getRuFormattedDate(record.day)), 1)
      ]),
      receptionTime: _withCtx(({ record }) => [
        _createTextVNode(_toDisplayString(getReceptionStatusTime(record)), 1)
      ]),
      editAction: _withCtx(({ record }) => [
        _createVNode(_unref(AButton), {
          disabled: record.id === appointmentToEdit.value?.id,
          "data-test-id": "edit-appointment-btn",
          onClick: ($event: any) => (onEditClick(record))
        }, {
          icon: _withCtx(() => [
            _createVNode(_unref(EditOutlined))
          ]),
          _: 2
        }, 1032, ["disabled", "onClick"])
      ]),
      removeAction: _withCtx(({ record }) => [
        _createVNode(_unref(AButton), {
          "data-test-id": "remove-appointment-btn",
          onClick: ($event: any) => (onRemoveClick(record))
        }, {
          icon: _withCtx(() => [
            _createVNode(_unref(DeleteOutlined))
          ]),
          _: 2
        }, 1032, ["onClick"])
      ]),
      _: 1
    }, 8, ["data-source"]),
    (recordsToShow.value < totalRecordsCount.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          _hoisted_6,
          _createVNode(_unref(AButton), {
            disabled: isFetching.value,
            "data-test-id": "load-more-appointments-btn",
            onClick: onShowMoreAppointmentsClick
          }, {
            default: _withCtx(() => [
              _createTextVNode(" Развернуть "),
              _createVNode(_unref(DownOutlined))
            ]),
            _: 1
          }, 8, ["disabled"]),
          _hoisted_7
        ]))
      : _createCommentVNode("", true),
    _createVNode(NextAppointment, {
      ref_key: "nextAppointmentRef",
      ref: nextAppointmentRef,
      value: appointmentToEdit.value,
      "patient-id": __props.patientId,
      "protocol-id": __props.protocolId,
      "is-read-mode": __props.isReadMode,
      onCancel: onCancelClick,
      onCreate: _unref(onAppointmentCreate),
      onUpdate: _unref(onAppointmentUpdate)
    }, null, 8, ["value", "patient-id", "protocol-id", "is-read-mode", "onCreate", "onUpdate"]),
    _createVNode(ModalController, {
      ref_key: "confirmationModalRef",
      ref: confirmationModalRef,
      width: 600,
      title: "Требуется подтверждение"
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(InfoCircleOutlined), { style: { color: '#1890ff', fontSize: '30px' } }),
        _createTextVNode("   " + _toDisplayString(conflictMessage.value) + " ", 1),
        (conflictedValueList.value.length)
          ? (_openBlock(), _createBlock(_component_a_table, {
              key: 0,
              style: { margin: '10px 0' },
              columns: conflictedColumns,
              pagination: false,
              "data-source": conflictedValueList.value,
              "row-key": "id",
              size: "small"
            }, null, 8, ["data-source"]))
          : _createCommentVNode("", true),
        _createTextVNode(" Всё равно " + _toDisplayString(conflictAction.value === 'DELETE' ? 'удалить' : 'сохранить') + " запись? ", 1)
      ]),
      _: 1
    }, 512)
  ]))
}
}

})